import { hasValue } from './objectUtils';

export const calculateResponseScore = (response) => {
    if (hasValue(response.scaleResponse)) {
        return response.scaleResponse;
    }
    if (response.choiceResponse?.length > 0) {
        return response.choiceResponse.reduce((prev, curr) => prev + (curr.score || 0), 0);
    }
    return 0;
};

export const calculateSubtotals = (taskResponses, taskSubtotals) => {
    const scoresByQuestionId = Object.keys(taskResponses)
        .filter(key => taskResponses[key]?.response && !taskResponses[key]?.isSubtotal)
        .reduce((prev, questionId) => ({ ...prev, [questionId]: calculateResponseScore(taskResponses[questionId].response) }), {});

    return taskSubtotals.map(({ id: taskSubtotalId, questionIds }) => {
        const subTotalSum = questionIds?.length > 0
            ? questionIds.reduce((sum, questionId) => sum + (scoresByQuestionId[questionId] || 0), 0)
            : Object.values(scoresByQuestionId).reduce((sum, score) => sum + score, 0);

        return {
            taskSubtotalId,
            response: { scaleResponse: subTotalSum },
        };
    });
};

export const getResponseHash = response => (response ? JSON.stringify({
    f: response.freetextResponse,
    c: response.choiceResponse,
    s: response.scaleResponse,
    t: response.tableResponses,
}) : null);

const reduceResponsesForTask = (newResponses, currentResponseList, taskId, taskVersion, patientProgramElementId, formId) => {
    if (!newResponses?.length) {
        return currentResponseList;
    }

    const existingResponsesForElement = (currentResponseList && currentResponseList[patientProgramElementId]) || {};
    const existingResponsesForTask = {
        ...existingResponsesForElement[taskId],
        patientProgramElementId,
        taskId,
        formId,
        taskVersion,
    };

    return {
        ...currentResponseList,
        [patientProgramElementId]: {
            ...existingResponsesForElement,
            [taskId]: {
                ...existingResponsesForTask,
                ...newResponses?.reduce((prev, curr) => ({
                    ...prev,
                    [curr.questionId || curr.taskSubtotalId]: {
                        response: curr.response,
                        isSubtotal: !!curr.taskSubtotalId,
                        changeKey: curr.changeKey,
                    },
                }), {}),
            },
        },
    };
};

export const parseInsertedResponse = (inputData, subtotals, currentResponses) => {
    const {
        patientProgramElementId, taskId, taskVersion, responses, formId,
    } = inputData;

    const newResponses = responses.map(({ questionId, ...response }) => ({
        changeKey: getResponseHash(response),
        questionId,
        response,
    }));

    let responseList = reduceResponsesForTask(newResponses, currentResponses, taskId, taskVersion, patientProgramElementId, formId);

    if (subtotals.length && responseList[patientProgramElementId] && responseList[patientProgramElementId][taskId]) {
        const newTaskSubtotalResponses = calculateSubtotals(responseList[patientProgramElementId][taskId], subtotals);
        responseList = reduceResponsesForTask(newTaskSubtotalResponses, responseList, taskId, taskVersion, patientProgramElementId, formId);
    }

    return responseList;
};

export const parseResponseSaveResult = (inputData, taskVisitResult, currentResponses) => {
    const {
        responses, taskVersion,
    } = inputData;
    const {
        newResponses, patientProgramElementId, taskId, formId,
    } = taskVisitResult;

    const savedResponses = responses?.map(({ questionId, ...response }) => ({
        changeKey: null,
        questionId,
        response,
    }))
    .filter((d) => { // avoid updating elements with "new" changes. Only include items with identical changeKey as in store
        if (!currentResponses || !currentResponses[patientProgramElementId] || !currentResponses[patientProgramElementId][taskId]
            || !currentResponses[patientProgramElementId][taskId][d.questionId]?.response) { return true; }
        if (currentResponses[patientProgramElementId][taskId][d.questionId].changeKey === getResponseHash(d.response)) { return true; }
        return false;
    })
    .concat(newResponses || []);

    return reduceResponsesForTask(savedResponses, currentResponses, taskId, taskVersion, patientProgramElementId, formId);
};

export const buildResponseLookup = (responseList, existingResponseList) => responseList?.reduce((prev, form) => {
    // Structure:
    // currentProgramTaskResponses[ppeId][taskId]
    // currentProgramTaskResponses[ppeId][taskId].patientProgramElementId;
    // currentProgramTaskResponses[ppeId][taskId].taskId;
    // currentProgramTaskResponses[ppeId][taskId].formId;
    // currentProgramTaskResponses[ppeId][taskId].taskVersion;
    // currentProgramTaskResponses[ppeId][taskId].submitted
    // currentProgramTaskResponses[ppeId][questionOrSubtotalId].response.scaleResponse;
    const formProps = { ...form };
    delete formProps.responses; // remove responses-list from props -> this is spread out on [questionId]

    const formVm = {
        ...formProps, // includes {patientProgramElementId, taskId, taskVersion, formId, submitted}
        ...form.responses?.reduce((rPrev, rCurr) => ({ // [questionId || subtotalId] as direct property
            ...rPrev,
            [rCurr.questionId || rCurr.taskSubtotalId]: {
                // could remove "response" prop and decompose all subProps directly. But must remove .response references a lot of places..
                response: rCurr.response,
                isSubtotal: !!rCurr.taskSubtotalId,
            },
        }), {}),
    };

    return {
        ...prev,
        [form.patientProgramElementId]: {
            ...prev[form.patientProgramElementId],
            [form.taskId]: formVm,
        },
    };
}, existingResponseList || {}) || existingResponseList || {};

export const getUpdatedResponsesForTask = (taskResponses, patientProgramElementId, taskId, taskVersion, formId) => {
    if (!taskResponses) { return null; }
    const updatedResponsesForTask = Object.keys(taskResponses).reduce((prevQuestions, currQuestionId) => (
        taskResponses[currQuestionId]?.changeKey && !taskResponses[currQuestionId].isSubtotal // Some keys in currTask are not subprops -> Can be null or undefined
            ? [...prevQuestions, {
                questionId: currQuestionId,
                ...taskResponses[currQuestionId].response,
            }] : prevQuestions
    ), []);

    if (updatedResponsesForTask.length) {
        const responseData = {
            patientProgramElementId,
            taskId,
            taskVersion,
            formId,
            responses: updatedResponsesForTask,
        };

        return responseData;
    }
    return null;
};

export const getSpecificTaskResponses = (existingResponseList, currentTaskResponse) => (taskId) => {
    if (!existingResponseList || !taskId) {
        return null;
    }

    if (currentTaskResponse?.taskId === taskId) {
        return currentTaskResponse;
    }

    return Object.keys(existingResponseList).reduce((prevPpe, currPpeId) => prevPpe
        || Object.keys(existingResponseList[currPpeId]).reduce((prevTask, currTaskId) => prevTask
            || (currTaskId === taskId ? existingResponseList[currPpeId][currTaskId] : null),
        null),
    null);
};
